import { useMemo } from "react";
import * as d3 from "d3";
import { InteractionData } from "../../index";
import useWindowSize from "hooks/useWindowSize";
import { formatMoney, formatMoneyHeatmap } from "helpers";


type RendererProps = {
  width: number;
  height: number;
  data: { x: string; y: string; value: number | null }[];
  setHoveredCell: (hoveredCell: InteractionData | null) => void;
};

export const Renderer = ({
  width,
  height,
  data,
  setHoveredCell,
}: RendererProps) => {
  // The bounds (=area inside the axis) is calculated by substracting the margins
  const { width:windowWidth } = useWindowSize();

  const MARGIN = width > 480? { top: 120, right: 50, bottom: 30, left: 150 } : { top: 5, right: 5, bottom: 5, left: 5 }

  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const allYGroups = useMemo(() => [...new Set(data.map((d) => d.y))], [data]);
  const allXGroups = useMemo(() => [...new Set(data.map((d) => d.x))], [data]);
  const [min = 0, max = 0] = d3.extent(data.map((d) => d.value ?? 0)); // extent can return [undefined, undefined], default to [0,0] to fix types

  const xScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([0, boundsWidth])
      .domain(allXGroups)
      .padding(-0.01)
      // .paddingInner(-0.01)
    }, [data, width]);

  const yScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([boundsHeight, 0])
      .domain(allYGroups)
      .padding(-0.01)
      // .paddingInner(-0.01)
  }, [data, height]);

  var colorScale = d3
    .scaleSequential()
    .interpolator(d3.interpolateRgb("#EBF78F", "#479D7F"))
    .domain([min, max]);

  // Build the rectangles
  const allShapes = data.map((d, i) => {
    const x = xScale(d.x);
    const y = yScale(d.y);

    return(
     <>
        <rect
          key={i}
          r={4}
          x={xScale(d.x)}
          y={yScale(d.y)}
          width={xScale.bandwidth()}
          height={yScale.bandwidth()}
          opacity={1}
          fill={d.value? colorScale(d.value) : 'white'}
          onMouseEnter={(e) => {
            setHoveredCell({
              xLabel: d.x,
              yLabel: d.y,
              xPos: (x ?? 0) + xScale.bandwidth() * 0.6,
              yPos: (y ?? 0) + xScale.bandwidth() * 0.8,
              value: d.value ?? 0,
            });
          }}
          onMouseLeave={() => setHoveredCell(null)}
          cursor="pointer"
        />
        <text
          cursor="pointer"
          onMouseEnter={(e) => {
            setHoveredCell({
              xLabel: d.x,
              yLabel: d.y,
              xPos: (x ?? 0) + xScale.bandwidth() * 0.6,
              yPos: (y ?? 0) + xScale.bandwidth() * 0.8,
              value: d.value ?? 0,
            });
          }}
          onMouseLeave={() => setHoveredCell(null)}
          x={(xScale(d.x) ?? 0) + xScale.bandwidth()/2}
          y={(yScale(d.y) ?? 0 ) + yScale.bandwidth()/7 * 4}
          fontWeight={d.value? '400' : '600'}
          fill={d.value? 'black' : '#B9261A'} font-size="12" text-anchor="middle" dominantBaseline="middle">{formatMoneyHeatmap(d.value, (width <= 480)) }</text>
     </>
    );
  });

  const isMobile = () => {
    return width <= 480
  }
  const xLabels = allXGroups.map((name, i) => {
    const x = xScale(name);

    if (!x) {
      return null;
    }

    return (
      <text
        key={i}
        x={x + xScale.bandwidth() / 2}
        y={-10}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={12}
        fill="black"
        transform={`rotate(-30, ${x + xScale.bandwidth() / 2}, -10)`}
      >
        {name}
      </text>
    );
  });

  const yLabels = allYGroups.map((name, i) => {
    const y = yScale(name);

    if (!y) {
      return null;
    }

    return (
      <text
        key={i}
        y={y + yScale.bandwidth() / 2}
        x={-10}
        textAnchor="end"
        dominantBaseline="middle"
        fontSize={12}
        fill="black"
      >
        {name.length > 20 ? name.substring(0,20) + '...' : name}
      </text>
    );
  });


  return (
    <svg width={width} height={height}>
      <g
        width={boundsWidth}
        height={boundsHeight}
        transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
      >
        {allShapes}
        {(windowWidth > 480) &&
        <>
        {xLabels}
        {yLabels}
        </>
        }
      
      </g>
    </svg>
  );
};
